import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { URL_CORE_PRICING_FOOTNOTES } from '../../../end-points';
import { SEARCH_STATUS } from '../../../utils/constants';
import { getApiHeaders } from '../../../utils/header-util';
import { NOTIFICATION_MESSAGES } from '../../../utils/notification-messages';
import { toastWarn } from '../../../utils/toasts';

const useSearch = (searchQuery, isAbortSearch) => {
   const [isLoading, setIsLoading] = useState(false);
   const [responseData, setResponseData] = useState(null);
   const [searchStatus, setSearchStatus] = useState(SEARCH_STATUS.IN_PROGRESS);
   const cancelApiTokenRef = useRef(null);
   const currentRequestRef = useRef(0);

   const cancelSearch = useCallback((showNotification = false) => {
      if (cancelApiTokenRef.current) {
         cancelApiTokenRef.current.abort();
         if (showNotification) {
            toastWarn(NOTIFICATION_MESSAGES.SEARCH_STOPPED);
         }
      }
   }, []);

   const fetchData = useCallback(
      async (
         payload,
         isNewSearch,
         currentRequestId,
         pageToken,
         bqJobInfo,
         prevResponseData
      ) => {
         try {
            const controller = new AbortController();
            cancelApiTokenRef.current = controller;

            const response = await axios.post(
               URL_CORE_PRICING_FOOTNOTES,
               { ...payload, pageToken, bqJobInfo: bqJobInfo },
               { signal: controller.signal, headers: getApiHeaders() }
            );

            if (currentRequestId !== currentRequestRef.current) return;

            const newRows = response.data.record2s.map((data, index) => ({
               ...data,
               id: (isNewSearch ? 0 : prevResponseData?.length || 0) + index,
            }));

            const updatedRows = isNewSearch
               ? newRows
               : [...prevResponseData, ...newRows];
            setResponseData(updatedRows);
            setIsLoading(false);
            pageToken = response.data.pageToken;
            bqJobInfo = response.data.jobId;

            if (!pageToken) {
               const isNoResults = !updatedRows?.length;
               setSearchStatus(
                  isNoResults
                     ? SEARCH_STATUS.NO_RESULTS
                     : SEARCH_STATUS.COMPLETED
               );
               cancelApiTokenRef.current = null;
               return;
            }
            const newRequestId = currentRequestRef.current + 1;
            currentRequestRef.current = newRequestId;

            fetchData(
               payload,
               false,
               newRequestId,
               pageToken,
               bqJobInfo,
               updatedRows
            );
         } catch (error) {
            console.warn('Error in fetching Rule and Footnote records', error);
            if (error.name !== 'CanceledError') {
               toastWarn(error.message);
               setIsLoading(false);
               setResponseData([]);
            }
         }
      },
      []
   );

   useEffect(() => {
      if (Object.keys(searchQuery).length > 0) {
         setIsLoading(true);
         cancelSearch();
         setResponseData([]);
         setSearchStatus(SEARCH_STATUS.IN_PROGRESS);
         const isNewSearch = true;
         fetchData(
            searchQuery,
            isNewSearch,
            currentRequestRef.current,
            null,
            null
         );
      }

      return () => {
         cancelSearch(); // cleanup API calls
      };
   }, [searchQuery, cancelSearch, fetchData]);

   useEffect(() => {
      if (isAbortSearch && cancelApiTokenRef.current) {
         setIsLoading(false);
         setSearchStatus(SEARCH_STATUS.ABORTED);
         const showNotification = true;
         cancelSearch(showNotification);
      }
   }, [isAbortSearch, cancelSearch]);

   return { isLoading, responseData, searchStatus };
};

export default useSearch;
